import { axiosConfigBuilder } from '..';

/**
 * Service class for interacting with MMM API endpoints.
 *
 * This service provides methods for requesting and updating
 * data related to the MMM ListView and MMM Client.
 */
export class MMMService {
  /**
   * Retrieves models based on the provided request parameters.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} modelRequestParams - Parameters to filter or request specific models.
   * @returns {Promise<Object>} A promise that resolves to the response data containing models.
   */
  async getModels(token, modelRequestParams) {
    const modelsResponse = await axiosConfigBuilder(token).post(
      '/get_models',
      modelRequestParams
    );
    return modelsResponse.data;
  }

  /**
   * Creates a new model with the provided details.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} newModelDetails - The details of the new model to be created.
   * @returns {Promise<Object>} A promise that resolves to the response data of the model creation.
   */
  async createNewModel(token, newModelDetails) {
    const modelCreationResponse = await axiosConfigBuilder(token).post(
      '/create_new_model',
      newModelDetails
    );
    return modelCreationResponse.data;
  }

  /**
   * Generates a config file for the specified model using the specified config details.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} configDetails - Details required for building the MMM config file.
   * @returns {Promise<Object>} A promise that resolves to the response data of the config creation.
   */
  async createConfigFileForMMM(token, configDetails) {
    const configCreationResponse = await axiosConfigBuilder(token).post(
      '/build_mmm_config',
      configDetails
    );
    return configCreationResponse.data;
  }

  /**
   * Updates the metadata associated with the specified model.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} newMetadataDetails - The new metadata details to update.
   * @returns {Promise<Object>} A promise that resolves to the updated metadata response data.
   */
  async updateMetadataRequest(token, newMetadataDetails) {
    const metadataResponse = await axiosConfigBuilder(token).post(
      '/update_mmm_metadata',
      newMetadataDetails
    );
    return metadataResponse.data;
  }

  /**
   * Removes a model based on the provided details.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} removeModelDetails - Details identifying the model to be removed.
   * @returns {Promise<Object>} A promise that resolves to the response data confirming deletion.
   */
  async removeModel(token, removeModelDetails) {
    const removedModelResponse = await axiosConfigBuilder(token).post(
      '/delete_model',
      removeModelDetails
    );
    return removedModelResponse.data;
  }

  /**
   * Toggles the favorite status of a model or folder.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} toggleFavoriteParams - Parameters required to toggle the favorite status.
   * @returns {Promise<Object>} A promise that resolves to the response data after toggling.
   */
  async toggleFavoriteStatus(token, toggleFavoriteParams) {
    const favoriteStatusResponse = await axiosConfigBuilder(token).post(
      '/toggle_favourite_mmm_or_folder',
      toggleFavoriteParams
    );
    return favoriteStatusResponse.data;
  }

  /**
   * Copies an existing model.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} copyModelDetails - Details required to copy the model.
   * @returns {Promise<Object>} A promise that resolves to the response data of the copied model.
   */
  async copyModel(token, copyModelDetails) {
    const copyModelResponse = await axiosConfigBuilder(token).post(
      '/copy_model',
      copyModelDetails
    );
    return copyModelResponse.data;
  }

  /**
   * Retrieves notes belonging to the specified model.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} notesRequestParams - Parameters to filter or specify which notes to retrieve.
   * @returns {Promise<Object>} A promise that resolves to the response data containing the notes.
   */
  async getNotes(token, notesRequestParams) {
    const notesResponse = await axiosConfigBuilder(token).post(
      '/retrieve_mmm_notes',
      notesRequestParams
    );
    return notesResponse.data;
  }

  /**
   * Creates or updates notes related to the specified model.
   *
   * @param {string} token - The authentication token for API access.
   * @param {Object} notesRequestParams - The notes details to create or update.
   * @returns {Promise<Object>} A promise that resolves to the response data after updating the notes.
   */
  async createOrUpdateNotes(token, notesRequestParams) {
    const notesCreatedResponse = await axiosConfigBuilder(token).post(
      '/update_mmm_notes',
      notesRequestParams
    );
    return notesCreatedResponse.data;
  }
}
